import { useCallback, useEffect, useState } from 'react'

import type { CatalogFreshnessStatus } from '../api/catalogTypes'
import { displaySupplierName } from '../utils/supplierName'
import {
  getRestaurantMyPricesSummary,
  listRestaurantMyPricesItems,
  listRestaurantPrivateSuppliers,
  listRestaurantPrivateSupplierStats,
  type RestaurantPrivateSupplierDto,
  type RestaurantPrivateSupplierStatsDto,
} from '../api/myPrices'

export type ImportStartMode =
  | { kind: 'new' }
  | { kind: 'update'; supplierId: number; supplierName: string }

type PricesScreenProps = {
  organizationId: number
  onStartImport: (mode: ImportStartMode) => void
}

type SupplierCardStats = {
  itemCount: number | null
  updatedLabel: string
  freshness: CatalogFreshnessStatus | null
}

type SupplierStatsAccumulator = {
  itemCount: number
  latestUpdatedAt: string | null
  latestFreshness: CatalogFreshnessStatus | null
}

const supplierLabel = (supplier: RestaurantPrivateSupplierDto): string =>
  displaySupplierName(supplier.displayLabel, supplier.legalName)

const supplierInitials = (name: string): string => {
  const cleaned = name.replace(/[«»"']/g, '').trim()
  if (!cleaned) {
    return '?'
  }
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
  }
  return cleaned.slice(0, 2).toUpperCase()
}

const formatUpdatedLabel = (iso: string | null): string => {
  if (!iso) {
    return '—'
  }
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

const freshnessLabel = (status: CatalogFreshnessStatus | null): { text: string; tone: 'ok' | 'warn' } => {
  if (status === 'fresh') {
    return { text: 'актуален', tone: 'ok' }
  }
  if (status === 'stale_7' || status === 'stale_14' || status === 'stale_30') {
    return { text: 'давно не обновлялся', tone: 'warn' }
  }
  return { text: '—', tone: 'warn' }
}

const isMoreRecent = (candidate: string | null, current: string | null): boolean => {
  if (!candidate) {
    return false
  }
  if (!current) {
    return true
  }
  return new Date(candidate).getTime() > new Date(current).getTime()
}

const statsRecord = (
  suppliers: RestaurantPrivateSupplierDto[],
  stats: RestaurantPrivateSupplierStatsDto[],
): Record<number, SupplierCardStats> => {
  const bySupplier = new Map(stats.map((entry) => [entry.privateSupplierId, entry]))
  const entries = suppliers.map((supplier) => {
    const row = bySupplier.get(supplier.id)
    return [
      supplier.id,
      {
        itemCount: row?.itemCount ?? 0,
        updatedLabel: formatUpdatedLabel(row?.lastUpdatedAt ?? null),
        freshness: row?.freshnessStatus ?? null,
      },
    ] as const
  })
  return Object.fromEntries(entries)
}

const buildStatsFromCatalogItems = async (
  organizationId: number,
  suppliers: RestaurantPrivateSupplierDto[],
): Promise<Record<number, SupplierCardStats>> => {
  const pageSize = 200
  const firstPage = await listRestaurantMyPricesItems({
    organizationId,
    page: 0,
    size: pageSize,
  })

  const pages = [firstPage]
  if (firstPage.totalPages > 1) {
    const tailPages = await Promise.all(
      Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
        listRestaurantMyPricesItems({
          organizationId,
          page: index + 1,
          size: pageSize,
        }),
      ),
    )
    pages.push(...tailPages)
  }

  const bySupplier = new Map<number, SupplierStatsAccumulator>()
  for (const page of pages) {
    for (const item of page.items) {
      if (item.privateSupplierId == null) {
        continue
      }
      const current = bySupplier.get(item.privateSupplierId) ?? {
        itemCount: 0,
        latestUpdatedAt: null,
        latestFreshness: null,
      }
      current.itemCount += 1
      if (isMoreRecent(item.updatedAt, current.latestUpdatedAt)) {
        current.latestUpdatedAt = item.updatedAt
        current.latestFreshness = item.freshnessStatus
      }
      bySupplier.set(item.privateSupplierId, current)
    }
  }

  const statsEntries = suppliers.map((supplier) => {
    const aggregate = bySupplier.get(supplier.id)
    return [
      supplier.id,
      {
        itemCount: aggregate?.itemCount ?? 0,
        updatedLabel: formatUpdatedLabel(aggregate?.latestUpdatedAt ?? null),
        freshness: aggregate?.latestFreshness ?? null,
      },
    ] as const
  })
  return Object.fromEntries(statsEntries)
}

export const PricesScreen = ({ organizationId, onStartImport }: PricesScreenProps) => {
  const [suppliers, setSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [totalItems, setTotalItems] = useState<number | null>(null)
  const [statsBySupplier, setStatsBySupplier] = useState<Record<number, SupplierCardStats>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [supplierList, summary] = await Promise.all([
        listRestaurantPrivateSuppliers(organizationId),
        getRestaurantMyPricesSummary(organizationId),
      ])
      setSuppliers(supplierList)
      setTotalItems(summary.totalItems)

      try {
        const supplierStats = await listRestaurantPrivateSupplierStats(organizationId)
        setStatsBySupplier(statsRecord(supplierList, supplierStats))
      } catch {
        const fallbackStats = await buildStatsFromCatalogItems(organizationId, supplierList)
        setStatsBySupplier(fallbackStats)
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить прайсы')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="rp-screen" aria-label="Мои прайсы">
      <button
        type="button"
        className="echat-btn echat-btn--primary"
        onClick={() => onStartImport({ kind: 'new' })}
      >
        ＋ Добавить новый прайс
      </button>

      <div className="rp-meta-line">
        <span>Мои прайсы поставщиков</span>
        {totalItems != null ? <span>{totalItems} поз. всего</span> : null}
      </div>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="echat-muted">Загрузка…</p> : null}

      {!loading && suppliers.length === 0 ? (
        <div className="echat-inbox__empty">
          <p className="echat-inbox__empty-title">Прайсов пока нет</p>
          <p className="echat-muted">
            Загрузите первый прайс поставщика — после импорта позиции появятся в поиске Провизиума.
          </p>
        </div>
      ) : null}

      <div className="rp-price-list">
        {suppliers.map((supplier) => {
          const name = supplierLabel(supplier)
          const stats = statsBySupplier[supplier.id]
          const fresh = freshnessLabel(stats?.freshness ?? null)
          return (
            <article key={supplier.id} className="rp-price-card">
              <div className="rp-price-card__top">
                <div className="rp-price-card__ava" aria-hidden="true">
                  {supplierInitials(name)}
                </div>
                <div className="rp-price-card__info">
                  <h3 className="rp-price-card__name">{name}</h3>
                  <p className="rp-price-card__inn">ИНН {supplier.inn || '—'}</p>
                </div>
                {stats?.freshness ? (
                  <span
                    className={
                      fresh.tone === 'ok'
                        ? 'rp-price-card__fresh rp-price-card__fresh--ok'
                        : 'rp-price-card__fresh rp-price-card__fresh--warn'
                    }
                  >
                    {fresh.text}
                  </span>
                ) : null}
              </div>
              <div className="rp-price-card__stats">
                <div>
                  <div className="rp-price-card__stat-n">
                    {stats?.itemCount != null ? stats.itemCount : '—'}
                  </div>
                  <div className="rp-price-card__stat-l">позиций</div>
                </div>
                <div>
                  <div className="rp-price-card__stat-n" style={{ fontSize: '0.8125rem', paddingTop: '2px' }}>
                    {stats?.updatedLabel ?? '—'}
                  </div>
                  <div className="rp-price-card__stat-l">обновлён</div>
                </div>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--inline rp-price-card__update"
                  onClick={() =>
                    onStartImport({
                      kind: 'update',
                      supplierId: supplier.id,
                      supplierName: name,
                    })
                  }
                >
                  Обновить
                </button>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
